import AxiosInstance from "../config/AxiosInstance";

/** Report type enum values matching backend FinancialReportTypeEnum */
export const ReportType = {
  IncomeStatement: 1,
  BalanceSheet: 2,
  CashFlow: 3,
  TrialBalance: 4,
  ArAgeing: 6,
  ApAgeing: 7,
  ControlAccountReconciliation: 8,
} as const;

/** Output type: View = 1, Pdf = 2, Excel = 3, Csv = 4 */
export const OutputType = {
  View: 1,
  Pdf: 2,
  Excel: 3,
  Csv: 4,
} as const;

/** Period mode: SingleMonth = 1, Quarter = 2, Year = 3, Range = 4 */
export const PeriodMode = {
  SingleMonth: 1,
  Quarter: 2,
  Year: 3,
  Range: 4,
} as const;

/** Compare mode: None = 1, PreviousPeriod = 2, SamePeriodLastYear = 3, CustomRange = 4 */
export const CompareMode = {
  None: 1,
  PreviousPeriod: 2,
  SamePeriodLastYear: 3,
  CustomRange: 4,
} as const;

export interface TransactionMonthOption {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  fiscalYear: string;
  monthNo: number;
}

export interface FinancialReportOptions {
  reportTypes: { value: number; label: string; labelKey: string; implemented: boolean }[];
  outputTypes: { value: number; label: string; labelKey: string }[];
  periodModes: { value: number; label: string; labelKey: string }[];
  compareModes: { value: number; label: string; labelKey: string }[];
  transactionMonths?: TransactionMonthOption[];
}

export interface GenerateReportPayload {
  companyId: string;
  countryId: string;
  reportType: number;
  outputType: number;
  periodMode: number;
  transactionMonthId?: string;
  fromTransactionMonthId?: string;
  toTransactionMonthId?: string;
  compareMode?: number;
  compareFromTransactionMonthId?: string;
  compareToTransactionMonthId?: string;
  locationId?: string;
  includeZeroLines?: boolean;
  includeDraft?: boolean;
  includeCancelled?: boolean;
  includeRejected?: boolean;
}

export interface ExportResponse {
  success: true;
  filename: string;
  contentType: string;
  base64?: string;
  message?: string;
}

const BASE = "/api/finex/financial-reports";

/**
 * Get report options (report types, period modes, transaction months, etc.).
 * Uses companyId; does not require countryId.
 */
export const getFinancialReportOptions = async (
  companyId: string,
  locationId?: string
): Promise<FinancialReportOptions> => {
  const { data } = await AxiosInstance.get(`${BASE}/options`, {
    params: { companyId, locationId },
  });
  return data;
};

/**
 * Get transaction months for the company (for period/range selection).
 */
export const getTransactionMonths = async (
  companyId: string,
  locationId?: string
): Promise<TransactionMonthOption[]> => {
  const { data } = await AxiosInstance.get(`${BASE}/transaction-months`, {
    params: { companyId, locationId },
  });
  return Array.isArray(data) ? data : [];
};

/**
 * Generate report (view or export). Request must include countryId from current company context.
 * Sends both countryId and country_id for backend compatibility (camelCase + snake_case).
 * For View, returns JSON view data; for Pdf/Excel/Csv, returns { success, filename, contentType, base64 }.
 */
export const generateFinancialReport = async (
  payload: GenerateReportPayload
): Promise<unknown> => {
  const body = {
    ...payload,
    countryId: payload.countryId,
    country_id: payload.countryId,
  };
  const { data } = await AxiosInstance.post(`${BASE}/generate`, body);
  return data;
};
